import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/stores/authStore';

interface InventoryItem {
  productId: string;
  productName: string;
  quantityOnHand: number;
  status: 'OK' | 'LOW' | 'OUT_OF_STOCK';
  lowThreshold: number;
  timestamp: string;
}

interface StockAlert {
  productName: string;
  status: string;
  message: string;
  severity: 'warning' | 'critical';
  timestamp: string;
}

export function useInventorySync(
  branchId: string,
  onInventoryUpdate?: (item: InventoryItem) => void,
  onStockAlert?: (alert: StockAlert) => void,
  onInventorySnapshot?: (items: InventoryItem[]) => void,
) {
  const socketRef = useRef<Socket | null>(null);
  const { isAuthenticated } = useAuthStore();

  const connect = useCallback(() => {
    if (!isAuthenticated || !branchId || socketRef.current?.connected) {
      return;
    }

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    // Avoid '//' when base is '/' (a protocol-relative URL would treat 'inventory' as a host)
    const socketBase = apiUrl.replace(/\/$/, '');
    const socket = io(`${socketBase}/inventory`, {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      console.log('Inventory WebSocket connected');
      socket.emit('subscribe-branch', { branchId });
    });

    socket.on('subscribed', (data) => {
      console.log('Subscribed to branch:', data);
    });

    socket.on('inventory-updated', (item: InventoryItem) => {
      console.log('Inventory updated:', item);
      onInventoryUpdate?.(item);
    });

    socket.on('inventory-snapshot', (data: { branchId: string; items: InventoryItem[] }) => {
      console.log('Inventory snapshot received:', data.items);
      onInventorySnapshot?.(data.items);
    });

    socket.on('stock-alert', (alert: StockAlert) => {
      console.log('Stock alert:', alert);
      onStockAlert?.(alert);
    });

    socket.on('disconnect', () => {
      console.log('Inventory WebSocket disconnected');
    });

    socket.on('error', (error) => {
      console.error('Inventory WebSocket error:', error);
    });

    socketRef.current = socket;

    return () => {
      socket.emit('unsubscribe-branch', { branchId });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [isAuthenticated, branchId, onInventoryUpdate, onStockAlert, onInventorySnapshot]);

  useEffect(() => {
    const cleanup = connect();
    return cleanup;
  }, [connect]);

  const requestSnapshot = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('request-snapshot', { branchId });
    }
  }, [branchId]);

  return {
    isConnected: socketRef.current?.connected ?? false,
    requestSnapshot,
  };
}
